<script setup lang="ts">
import { computed, useAttrs, useCssModule, watchEffect } from 'vue';

import type { IconSize } from '../../types';
import type { ButtonProps } from '../../types/button';
import { cn } from '../../utils/cn';
import N8nIcon from '../N8nIcon';

const $style = useCssModule();
const attrs = useAttrs();

defineOptions({
	name: 'N8nButton',
	inheritAttrs: false,
});

const props = withDefaults(defineProps<ButtonProps>(), {
	variant: 'solid',
	size: 'medium',
	loading: false,
	disabled: false,
});

// Map legacy size values to current ones
const effectiveSize = computed(() => {
	if (props.size === 'mini' || props.size === 'xmini') return 'xsmall';
	return props.size;
});

// Map legacy variant values to current ones
const effectiveVariant = computed(() => {
	if (props.variant === 'highlight') return 'ghost';
	if (props.variant === 'highlight-fill') return 'ghost';
	return props.variant;
});

const computedIconSize = computed((): IconSize | undefined => {
	if (props.iconSize) return props.iconSize;
	if (effectiveSize.value === 'xsmall') return 'xsmall';
	return effectiveSize.value as IconSize;
});

const componentTag = computed(() => {
	if (props.href) return 'a';
	return 'button';
});

const buttonType = computed(() => {
	if (componentTag.value === 'a') return undefined;
	return (attrs.type as string | undefined) ?? 'button';
});

const isDisabled = computed(() => props.disabled || props.loading);

const classes = computed(() =>
	cn(
		'button',
		$style.button,
		$style[effectiveVariant.value],
		$style[effectiveSize.value],
		props.loading && $style.loading,
		props.iconOnly && $style.iconOnly,
		props.disabled && $style.disabled,
		isDisabled.value && 'is-disabled',
		props.class,
	),
);

// Accessibility warning for icon-only buttons without accessible labels
if (import.meta.env.DEV) {
	watchEffect(() => {
		if (props.iconOnly && !attrs['aria-label'] && !attrs.title) {
			console.warn(
				'[N8nButton] Icon-only buttons should have an accessible label. ' +
					'Add aria-label or title attribute.',
			);
		}
	});
}

const handleClick = (event: MouseEvent) => {
	if (props.href && isDisabled.value) {
		event.preventDefault();
	}
};
</script>

<template>
	<component
		:is="componentTag"
		v-bind="attrs"
		:type="buttonType"
		:href="href"
		:rel="href ? 'nofollow noopener noreferrer' : undefined"
		:disabled="componentTag === 'button' ? isDisabled || undefined : undefined"
		:aria-disabled="isDisabled || undefined"
		:aria-busy="loading || undefined"
		:tabindex="componentTag === 'a' && isDisabled ? -1 : undefined"
		:class="classes"
		:data-icon-only="iconOnly ? 'true' : undefined"
		aria-live="polite"
		@click="handleClick"
	>
		<Transition name="n8n-button-fade">
			<div v-if="loading" :class="$style['loading-container']">
				<div :class="[$style['loading-spinner'], 'n8n-spinner']">
					<N8nIcon icon="loader" :size="computedIconSize" transform-origin="center" />
				</div>
			</div>
		</Transition>

		<div :class="$style['button-inner']">
			<slot name="icon" v-if="!loading">
				<N8nIcon v-if="icon && !loading" :icon="icon" :size="computedIconSize" />
			</slot>

			<span v-if="label">{{ label }}</span>
			<slot v-else />
		</div>
	</component>
</template>

<style lang="scss">
// Import legacy override classes (n8n-button--success, n8n-button--warning, etc.)
// These are global classes added by the migration codemod for legacy button types
@use './Button.legacy';
</style>

<style lang="scss" module>
@use '../../css/mixins/focus';
@use '../../css/mixins/motion';

.button {
	appearance: none;
	touch-action: manipulation;
	-webkit-tap-highlight-color: transparent;
	user-select: none;
	width: fit-content;
	display: grid;
	align-items: center;
	font-weight: var(--font-weight--medium);
	line-height: 1;
	cursor: pointer;
	text-decoration: none;

	height: var(--button--height);
	padding: var(--button--padding);
	border-radius: var(--button--radius);
	font-size: var(--button--font-size);

	--button--color--background: transparent;
	--button--color--background-hover: var(--background--hover);
	--button--color--background-active: var(--background--active);
	--button--color: var(--text-color);
	--button--shadow: 0 0 0 0 transparent;
	--button--shadow--hover: 0 0 0 0 transparent;
	--button--shadow--active: 0 0 0 0 transparent;
	--button--border--shadow: 0 0 0 0 transparent;
	--button--border--shadow--hover: 0 0 0 0 transparent;
	--button--border--shadow--active: 0 0 0 0 transparent;
	--button--border-color: transparent;
	--button--border-color--hover: transparent;
	--button--border-color--active: transparent;

	background-color: var(--button--color--background);
	color: var(--button--color);
	box-shadow:
		inset var(--button--border--shadow),
		var(--button--shadow);
	border: none;

	> * {
		grid-area: 1 / 1;
	}

	&:hover {
		background-color: var(--button--color--background-hover);
		box-shadow:
			inset var(--button--border--shadow--hover),
			var(--button--shadow--hover);
	}

	&:active,
	&[aria-expanded='true'],
	:global([aria-expanded='true']) & {
		background-color: var(--button--color--background-active);
		box-shadow:
			inset var(--button--border--shadow--active),
			var(--button--shadow--active);
	}

	&:focus {
		outline: none;
	}

	&:focus-visible {
		@include focus.focus-ring;
		--button--border-color: var(--focus--border-color) !important;
	}

	&.xsmall {
		--button--height: var(--height--xs);
		--button--padding: 0 var(--spacing--2xs);
		--button--radius: var(--radius--3xs);
		--button--font-size: var(--font-size--2xs);
	}

	&.small {
		--button--height: var(--height--sm);
		--button--padding: 0 var(--spacing--xs);
		--button--radius: var(--radius--3xs);
		--button--font-size: var(--font-size--xs);
	}

	&.medium {
		--button--height: var(--height--md);
		--button--padding: 0 var(--spacing--xs);
		--button--radius: var(--radius--3xs);
		--button--font-size: var(--font-size--sm);
	}

	&.large {
		--button--height: var(--height--lg);
		--button--padding: 0 var(--spacing--sm);
		--button--radius: var(--radius--2xs);
		--button--font-size: var(--font-size--sm);
	}

	&.xlarge {
		--button--height: var(--height--xl);
		--button--padding: 0 var(--spacing--sm);
		--button--radius: var(--radius--xs);
		--button--font-size: var(--font-size--md);
	}

	&.solid {
		--button--color--background: var(--background--brand);
		--button--color--background-hover: var(--background--brand--hover);
		--button--color--background-active: var(--background--brand--active);
		--button--color: var(--color--neutral-white);
		--button--shadow: var(--shadow--xs);
		--button--shadow--hover: var(--shadow--xs);
		--button--shadow--active: var(--shadow--xs);
		--button--border-color: var(--background--brand);
		--button--border-color--hover: var(--background--brand--hover);
		--button--border-color--active: var(--background--brand--active);
		--button--border--shadow: 0 0 0 1px var(--button--border-color);
		--button--border--shadow--hover: 0 0 0 1px var(--button--border-color--hover);
		--button--border--shadow--active: 0 0 0 1px var(--button--border-color--active);
	}

	&.subtle {
		--button--color--background: var(--background--surface);
		--button--color--background-hover: color-mix(
			in srgb,
			var(--button--color--background),
			light-dark(var(--color--neutral-black), var(--color--neutral-white)) 5%
		);
		--button--color--background-active: color-mix(
			in srgb,
			var(--button--color--background),
			light-dark(var(--color--neutral-black), var(--color--neutral-white)) 10%
		);
		--button--shadow: var(--shadow--xs);
		--button--shadow--hover: var(--shadow--xs);
		--button--shadow--active: var(--shadow--xs);
		--button--border-color: var(--border-color);
		--button--border-color--hover: light-dark(
			var(--color--black-alpha-200),
			var(--color--white-alpha-200)
		);
		--button--border-color--active: light-dark(
			var(--color--black-alpha-300),
			var(--color--white-alpha-300)
		);
		--button--border--shadow: 0 0 0 1px var(--button--border-color);
		--button--border--shadow--hover: 0 0 0 1px var(--button--border-color--hover);
		--button--border--shadow--active: 0 0 0 1px var(--button--border-color--active);
	}

	&.outline {
		--button--color--background: transparent;
		--button--border-color: var(--border-color);
		--button--border-color--hover: light-dark(
			var(--color--black-alpha-200),
			var(--color--white-alpha-200)
		);
		--button--border-color--active: light-dark(
			var(--color--black-alpha-300),
			var(--color--white-alpha-300)
		);
		--button--border--shadow: 0 0 0 1px var(--button--border-color);
		--button--border--shadow--hover: 0 0 0 1px var(--button--border-color--hover);
		--button--border--shadow--active: 0 0 0 1px var(--button--border-color--active);
	}

	&.ghost {
		--button--color--background: transparent;
		--button--border-color: transparent;
		--button--border--shadow: 0 0 0 1px var(--button--border-color);
		--button--border--shadow--hover: 0 0 0 1px var(--button--border-color);
		--button--border--shadow--active: 0 0 0 1px var(--button--border-color);
	}

	&.destructive {
		--button--color--background: light-dark(var(--color--red-500), var(--color--red-600));
		--button--color--background-hover: light-dark(var(--color--red-600), var(--color--red-500));
		--button--color--background-active: light-dark(var(--color--red-600), var(--color--red-400));
		--button--color: var(--color--neutral-white);
		--button--shadow: light-dark(
			0 1px 3px 0 var(--color--black-alpha-100),
			0 1px 3px 0 var(--color--black-alpha-200)
		);
		--button--shadow--hover: light-dark(
			0 1px 3px 0 var(--color--black-alpha-100),
			0 1px 3px 0 var(--color--black-alpha-200)
		);
		--button--shadow--active: light-dark(
			0 1px 3px 0 var(--color--black-alpha-100),
			0 1px 3px 0 var(--color--black-alpha-200)
		);
		--button--border-color: light-dark(var(--color--red-500), var(--color--red-600));
		--button--border-color--hover: light-dark(var(--color--red-600), var(--color--red-500));
		--button--border-color--active: light-dark(var(--color--red-600), var(--color--red-400));
		--button--border--shadow: 0 0 0 1px var(--button--border-color);
		--button--border--shadow--hover: 0 0 0 1px var(--button--border-color--hover);
		--button--border--shadow--active: 0 0 0 1px var(--button--border-color--active);
	}

	&.success {
		--button--color--background: light-dark(var(--color--green-600), var(--color--green-700));
		--button--color--background-hover: light-dark(var(--color--green-700), var(--color--green-600));
		--button--color--background-active: light-dark(
			var(--color--green-700),
			var(--color--green-500)
		);
		--button--color: var(--color--neutral-white);
		--button--shadow: light-dark(
			0 1px 3px 0 var(--color--black-alpha-100),
			0 1px 3px 0 var(--color--black-alpha-200)
		);
		--button--shadow--hover: light-dark(
			0 1px 3px 0 var(--color--black-alpha-100),
			0 1px 3px 0 var(--color--black-alpha-200)
		);
		--button--shadow--active: light-dark(
			0 1px 3px 0 var(--color--black-alpha-100),
			0 1px 3px 0 var(--color--black-alpha-200)
		);
		--button--border-color: light-dark(var(--color--green-600), var(--color--green-700));
		--button--border-color--hover: light-dark(var(--color--green-700), var(--color--green-600));
		--button--border-color--active: light-dark(var(--color--green-700), var(--color--green-500));
		--button--border--shadow: 0 0 0 1px var(--button--border-color);
		--button--border--shadow--hover: 0 0 0 1px var(--button--border-color--hover);
		--button--border--shadow--active: 0 0 0 1px var(--button--border-color--active);
	}

	&.disabled {
		opacity: 0.5;
		cursor: not-allowed;

		&:hover {
			background-color: var(--button--color--background);
			box-shadow:
				inset var(--button--border--shadow),
				var(--button--shadow);
		}
	}

	&.loading {
		pointer-events: none;
	}

	&.iconOnly {
		width: var(--button--height);
		padding: 0;
		justify-content: center;
		align-items: center;

		> * {
			width: var(--button--height);
		}
	}
}

.loading-container {
	height: auto;
	display: flex;
	align-items: center;
	justify-content: center;
}

.button-inner {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--spacing--3xs);
	white-space: nowrap;

	/** NOTE (@heymynameisrob): Covers legacy prop label which wraps in span **/
	> span {
		white-space: nowrap;
	}
}

.loading-container + .button-inner {
	pointer-events: none;
	opacity: 0;
}

.loading-spinner {
	display: flex;
	align-items: center;
	justify-content: center;
	@include motion.spin;
}

/* TODO: Move to global animations css library */
:global(.n8n-button-fade-enter-active),
:global(.n8n-button-fade-leave-active) {
	--easing--ease-out: cubic-bezier(0.215, 0.61, 0.355, 1);
	transition:
		opacity 0.2s var(--easing--ease-out),
		transform 0.2s var(--easing--ease-out);

	@media (prefers-reduced-motion: reduce) {
		transition: opacity 0.1s;
	}
}

:global(.n8n-button-fade-enter-from),
:global(.n8n-button-fade-leave-to) {
	opacity: 0;
	transform: translateY(4px);
	filter: blur(2px);

	@media (prefers-reduced-motion: reduce) {
		transform: none;
		filter: none;
	}
}
</style>
